<template>
  <section class="section-headline">
    <div class="section-headline-inner">
      <div class="headline-inner">
        <h1>{{ $t("Phones.Title") }}</h1>

        <mq-layout class="headline-controls" mq="lg">
          <div class="headline-switches">
            <b-field>
              <b-tooltip position="is-bottom" type="is-dark" multilined>
                  <b-switch :value="!showGraphs" @input="onShowGraphsChange($event)">{{ $t("Leads.HideGraphs") }}</b-switch>
                  <template v-slot:content>
                  <p>{{ $t("Phones.Tooltips.Headline.HideGraph") }}</p>
                  </template>
              </b-tooltip>
            </b-field>
          </div>

          <div class="headline-actions">
            <b-tooltip v-if="hasPhonesSyncRole" position="is-bottom" type="is-dark" multilined class="sync-button-w">
              <b-button type="is-primary" class="is-icon" @click="syncPhones">
                <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="search"><icon-search /></icon-base>
                <span>{{ $t("Phones.SyncPhones") }}</span>
              </b-button>
              <template v-slot:content>
                <p>{{ $t("Phones.SyncPhones") }}</p>
              </template>
            </b-tooltip>
            <b-tooltip position="is-bottom" type="is-dark" multilined>
              <b-button type="is-primary" class="is-icon" @click="showAddPhonePopup">
                <icon-base width="10" height="10" view-box="0 0 10 10" icon-name="plus"><icon-plus /></icon-base>
                <span>{{ $t("Phones.Add") }}</span>
              </b-button>
              <template v-slot:content>
                <p>{{ $t("Phones.Tooltips.Headline.Add") }}</p>
              </template>
            </b-tooltip>            
          </div>
        </mq-layout>

        <mq-layout class="headline-filters-toggler" :mq="['sm', 'md']">
          <button type="button" class="is-transparent button-text-w" @click="showMobileFilters">
            <icon-base width="22.22" height="20" view-box="0 0 22.22 20" icon-name="user-search"><icon-user-search /></icon-base>
            <span class="text">{{ $t("Filters") }}</span>
          </button>
        </mq-layout>
      </div>

      <mq-layout class="headline-controls-m" :mq="['sm', 'md']">
        <div class="headline-actions">
          <b-button v-if="hasPhonesSyncRole" type="is-primary" class="is-icon sync-button-w" size="is-small" @click="syncPhones">
            <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="search"><icon-search /></icon-base>
            <span>{{ $t("Phones.SyncPhones") }}</span>
          </b-button>
          <b-button type="is-primary" class="is-icon" size="is-small" @click="showAddPhonePopup">
            <icon-base width="10" height="10" view-box="0 0 10 10" icon-name="plus"><icon-plus /></icon-base>
            <span>{{ $t("Phones.Add") }}</span>
          </b-button>
        </div>

        <div class="headline-switches">
          <b-field>
            <b-switch :value="!showGraphs" @input="onShowGraphsChange($event)">{{ $t("Leads.HideGraphs") }}</b-switch>
          </b-field>
        </div>
      </mq-layout>
    </div>
    <transition name="fade">
      <div v-if="mobileFilters" class="headline-filters-m" :class="{ active: mobileFilters }">
        <div class="headline-filters-m-container">
          <div class="headline-filters-m-h">
            <div class="headline-filters-m-title">
              <icon-base width="22.22" height="20" view-box="0 0 22.22 20" icon-name="user-search"><icon-user-search /></icon-base>
              <span class="text">{{ $t("Filters") }}</span>
            </div>
            <div class="headline-filters-m-close" @click="hideMobileFilters">
              <icon-base width="16" height="16" view-box="0 0 7 7" icon-name="times"><icon-times /></icon-base>
            </div>
          </div>
          <div class="headline-filters-m-b">
            <div class="headline-filters-m-filters">
              <PhonesFiltersDropdowns />
              <PhonesFiltersDate />

              <div class="input-w">
                <b-field class="search-container">
                  <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="search"><icon-search /></icon-base>
                  <b-input :value="searchValue" :placeholder="$t('Grid.Search')"
                        @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"></b-input>
                </b-field>
              </div>
            </div>
            <div class="buttons">
              <b-button type="is-primary" expanded @click="onFilterClick()">{{ $t("Filter") }}</b-button>
            </div>
          </div>
        </div>
        <div class="headline-filters-m-bg"></div>
      </div>
    </transition>
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { campaignCreateRoute } from "@/router";
import PhonesFiltersDropdowns from "@/components/Phones/PhonesFiltersDropdowns.vue";
import PhonesFiltersDate from "@/components/Phones/PhonesFiltersDate.vue";
import { RefreshGridMode } from "@/models/leads/Lead";
import { UserData } from "@/models/UserData";
import AddPhoneDlg from "./AddPhoneDlg.vue";
import IconBase from "../IconBase.vue";
import IconPlus from "../icons/IconPlus.vue";
import IconUserSearch from "../icons/IconUserSearch.vue";
import IconTimes from "../icons/IconTimes.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";
import PhonesApiService from "@/services/api/PhonesApiService";
import HelperService from "@/services/HelperService";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "phonesModule";

@Component({
  components: {
    PhonesFiltersDropdowns,
    PhonesFiltersDate,
    IconBase,
    IconPlus,
    IconUserSearch,
    IconTimes,
    IconSearch,
    IconCaretLeft,
  },
})
export default class PhonesHeadline extends Vue {

  //#region Vuex getters and actions

  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Getter("userData", { namespace: "mainModule" }) userData!: UserData;
  @Getter("hasPhonesSyncRole", { namespace: "mainModule" }) hasPhonesSyncRole!: boolean;
  @Action("setNeedToRefreshPhonesGrid", { namespace }) setNeedToRefreshPhonesGrid!: (needToRefreshPhonesGrid: RefreshGridMode) => void;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;
  @Action("setShowGraphs", { namespace }) setShowGraphs!: (showGraphs: boolean) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  public mobileFilters = false;

  //#endregion Properties


  //#region Getters

  get campaignCreateRouteValue(): string {
    return campaignCreateRoute;
  }

  //#endregion Getters


  //#region Handlers

  public hideMobileFilters(): void {
    this.mobileFilters = false;
    this.popupBodyOff();
  }

  public onFilterClick(): void {
    this.setNeedToRefreshPhonesGrid(RefreshGridMode.Full);
    this.hideMobileFilters();
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  public onShowGraphsChange(hideGraphs: boolean): void {
    this.setShowGraphs(!hideGraphs);
  }

  public showAddPhonePopup(): void {
    this.$buefy.modal.open({
      parent: this,
      component: AddPhoneDlg,
      hasModalCard: true,
      trapFocus: true,
    });
  }

  public showMobileFilters(): void {
    this.mobileFilters = true;
    this.popupBodyOn();
  }

  public async syncPhones(): Promise<void> {
    let loadingComponent: any = null;
    try {
      // Show loading state
      loadingComponent = this.$buefy.loading.open({
        container: this.$el
      });

      // Call the sync API
      const result = await PhonesApiService.syncWithMaskyoo(
        this.userData.CompanyId
      );

      if (result.Success) {
        // Show success message
        this.$buefy.toast.open({
          message: result.Message || this.$t("Success").toString(),
          type: 'is-success',
          duration: 3000
        });

        // Refresh the phones grid
        this.setNeedToRefreshPhonesGrid(RefreshGridMode.Full);
      } else {
        // Show error message
        this.$buefy.toast.open({
          message: result.Message || this.$t("Error").toString(),
          type: 'is-danger',
          duration: 5000
        });
      }
    } catch (error) {
      // Show error message
      this.$buefy.toast.open({
        message: this.$t("Error").toString(),
        type: 'is-danger',
        duration: 5000
      });
    } finally {
      // Hide loading state
      if (loadingComponent) {
        loadingComponent.close();
      }
    }
  }

  //#endregion Handlers


  //#region Private methods

  public popupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public popupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-headline {
  .headline-inner {
    display: flex;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #d3d6e2;
    @media (max-width: 991px) {
      height: 52px;
    }
  }
  h1 {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    margin-bottom: 3px;
    @media (max-width: 991px) {
      font-size: 20px;
    }
  }

  .headline-controls {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    font-size: 16px;

    align-items: center;
    margin-right: auto;

    .headline-switches {
      .field {
        margin: 3px 18px 3px 0;
        display: flex;
      }
      .switch {
      }
      .check {
        .control-label {
          white-space: nowrap;
        }
      }
    }
    .headline-actions {
      margin-right: 30px;
      .button {
        height: 36px;
        &:not(:last-child) {
          margin-left: 15px;
        }
      }
      .sync-button-w {
        margin-left: 15px;
      }
    }
  }
  .headline-controls-m {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    flex-wrap: wrap;
    font-size: 16px;
    align-items: center;
    @media (max-width: 359px) {
      // display: block;
    }
    .headline-switches {
      // margin-right: 30px;
      .field {
        margin: 3px 0 3px 0;
        display: flex;
      }
      .switch {
      }
      .check {
        .control-label {
          white-space: nowrap;
        }
      }
    }
    .headline-actions {
      .button {
        border-radius: 6px;
        height: 30px;
        margin-left: 13px;
      }
      .sync-button-w {
        margin-left: 13px;
      }
    }
  }
  .headline-filters-toggler {
    margin-right: auto;
    margin-left: -5px;

    @media (min-width: 992px) {
      display: none;
    }
    .button-text-w {
      transition: all 0.2s;
      &:hover {
        color: $primary;
      }
      .icon-w + .text {
        margin-right: 7px;
      }
    }
  }

  .headline-switches-m {
    display: flex;
    flex-wrap: wrap;
    font-size: 16px;
    align-items: center;
    padding: 10px 0;
    justify-content: flex-start;
    @media (max-width: 399px) {
      justify-content: space-between;
    }
    @media (max-width: 359px) {
      display: block;
    }
    .field {
      margin: 5px 0 5px 0;
      display: flex;
      position: relative;
    }
    .border {
      display: block;
      width: 1px;
      height: 16px;
      background: $lightd;
      margin: 0 30px;
      @media (max-width: 399px) {
        margin: 0 10px;
      }
      @media (max-width: 359px) {
        display: none;
      }
    }
    .switch {
      direction: ltr;
      @media (max-width: 359px) {
        width: 100%;
        justify-content: space-between;
      }
    }
    .check {
      direction: rtl;
      .control-label {
        white-space: nowrap;
      }
    }
  }

  .headline-filters-m {
    position: fixed;
    z-index: -1;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    .headline-filters-m-container {
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 10;
      transition: all 0.5s;
      transform: translateX(0);
      background: #f6f7fb;
    }
    .headline-filters-m-h {
      position: fixed;
      top: 0;
      left: 0;
      height: 56px;
      width: 100%;
      color: #fff;
      display: flex;
      align-items: center;
      padding: 12px 15px 13px 10px;
      background: linear-gradient(90deg, $primary 0%, #1ca8d9 52.75%);
      .headline-filters-m-close {
        padding: 5px;
        cursor: pointer;
        line-height: 0;
        margin-right: auto;
        transition: all 0.2s;
        &:hover {
          opacity: 0.8;
        }
        .icon-w {
          svg {
          }
        }
      }
      .headline-filters-m-title {
        display: flex;
        align-items: center;
        font-size: 21px;
        .icon-w {
          margin-left: 10px;
        }
      }
    }
    .headline-filters-m-b {
      position: fixed;
      top: 56px;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 0 15px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      .headline-filters-m-switches {
        padding: 20px 0;
        border-bottom: 1px solid $lightd;
        .switch {
          width: 100%;
          justify-content: space-between;
        }
      }
      .headline-filters-m-filters {
        padding: 20px 0 0;
        display: flex;
        flex-wrap: wrap;
        margin: 0 -8px;
        .dropdown-w {
          padding: 0 8px 15px;
          .dropdown {
            width: 100%;
            .dropdown-trigger {
              width: 100%;
              .button {
                width: 100%;
              }
            }
          }
          &.datepicker-w {
            width: 100%;
            flex: 0 0 100%;
            .datepicker-w-i {
              position: relative;
            }
            .dropdown {
              width: 100%;
              .dropdown-trigger {
                width: 100%;
                .control {
                  width: 100%;
                }
              }
            }
          }
        }
        .input-w {
          padding: 0 8px 15px;
          width: 100%;
          .field.has-addons {
          }
        }
      }
      .buttons {
        margin: auto 0 0;
      }
    }
    .headline-filters-m-bg {
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 9;
      transition: all 0.5s;
      background: rgba(0, 0, 0, 0.3);
    }
    .buttons {
      padding-bottom: 30px;
      .button {
        margin: 0;
      }
    }

    &.fade-enter-active,
    &.fade-leave-active {
      z-index: 101;
      transition: all 0.5s;
    }
    &.fade-enter,
    &.fade-leave-to,
    &.fade-leave-active {
      opacity: 1;
      .headline-filters-m-container {
        transform: translateX(-100%);
      }
      .headline-filters-m-bg {
        opacity: 0;
      }
    }
    &.fade-enter {
    }
    &.active {
      z-index: 101;
      .headline-filters-m-container {
        // transform: translateX(0);
      }
    }
  }
}
html[dir="ltr"] {
  .section-headline {
    .headline-controls {
      margin: 0 0 0 auto;
      .headline-switches {
      }
      .headline-actions {
        margin: 0 0 0 30px;
        .button:not(:last-child) {
          margin: 0 15px 0 0;
        }
        .sync-button-w {
            margin-right: 15px;
            margin-left: 0;
        }
      }
    }
    .headline-controls-m {
      .headline-switches {
        // margin: 0 0 0 30px;
        .field {
          margin: 3px 0 3px 18px;
        }
      }
      .headline-actions {
        .button {
          margin: 0 13px 0 0;
        }
        .sync-button-w {
            margin-right: 13px;
            margin-left: 0;
        }
      }
    }
    .headline-filters-toggler {
      margin: 0 -5px 0 auto;
      .button-text-w .icon-w + .text {
        margin: 0 0 0 7px;
      }
    }
    .headline-filters-m {
      .headline-filters-m-h {
        .headline-filters-m-close {
          margin: 0 0 0 auto;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";
.section-headline {
  .headline-filters-m {
    .headline-filters-m-b {
      .headline-filters-m-filters {
        .dropdown-w {
          @media (max-width: 575px) {
            width: 100%;
            flex: 0 0 100%;
          }
        }
        .input-w {
          padding: 0 8px 15px;
          width: 100%;
          .field.has-addons {
            position: relative;
            display: block;
            .icon-w {
              position: absolute;
              top: 50%;
              right: 8px;
              transform: translateY(-50%);
              color: $dark;
              z-index: 5;
            }
            .input {
              padding: 5px 32px 5px 10px;
              border-radius: 4px;
            }
          }
        }
      }
    }
  }
}

html[dir="ltr"] {
  .section-headline {
    .headline-switches {
      margin: 0 0 0 auto;
      .field {
        margin: 3px 0 3px 18px;
      }
    }
  }
}
</style>

function Action(arg0: string, arg1: { namespace: any; }) {
  throw new Error("Function not implemented.");
}
